import React, {
  useState,
  useMemo,
  forwardRef,
  useImperativeHandle,
} from "react";
import { Box, Typography } from "@mui/material";
import * as XLSX from "xlsx";

const MaternityLeaveRequestsByPeriodTable = forwardRef(
  ({ data, sortBy = "period" }, ref) => {
    useImperativeHandle(ref, () => ({
      exportToExcel,
    }));

    const processedData = useMemo(() => {
      if (
        !data?.maternityLeaveRequestsByPeriod ||
        !data?.maternityLeaveReplacingStaffRequestsByPeriod
      ) {
        return null;
      }

      const requestsData = data.maternityLeaveRequestsByPeriod;
      const replacingStaffData =
        data.maternityLeaveReplacingStaffRequestsByPeriod;

      const combinedData = requestsData.map((request) => {
        const replacingStaff = replacingStaffData.find(
          (staff) =>
            staff.Period === request.Period &&
            staff.Year === request.Year &&
            staff.Month === request.Month
        );

        return {
          ...request,
          replacingStaff: replacingStaff || {
            ApprovedByDDE_A0: 0,
            ApprovedByDDE_A1: 0,
            ApprovedByDDE_A2: 0,
            ApprovedByDDE_Total: 0,
            PendingApproval_A0: 0,
            PendingApproval_A1: 0,
            PendingApproval_A2: 0,
            PendingApproval_Total: 0,
          },
        };
      });

      return combinedData;
    }, [data]);

    const filteredData = useMemo(() => {
      if (!processedData) return [];

      let filtered = [...processedData];

      filtered.sort((a, b) => {
        if (sortBy === "period") {
          if (a.Year !== b.Year) {
            return a.Year - b.Year;
          }
          return a.Month - b.Month;
        } else if (sortBy === "period_desc") {
          if (a.Year !== b.Year) {
            return b.Year - a.Year;
          }
          return b.Month - a.Month;
        }
        return 0;
      });

      return filtered;
    }, [processedData, sortBy]);

    const exportToExcel = () => {
      if (!processedData || processedData.length === 0) return;

      const wb = XLSX.utils.book_new();
      const excelData = [];

      const headerRow1 = [
        "Period",
        "Maternity Leave requests",
        "",
        "",
        "",
        "",
        "",
        "",
        "",
        "",
        "",
        "",
        "",
        "Waiting to be placed",
        "",
        "",
        "",
        "",
        "",
        "",
        "",
        "",
        "",
        "",
        "",
        "Placed candidates",
        "",
        "",
        "",
      ];

      const headerRow2 = [
        "",
        "Approved by HT",
        "",
        "",
        "",
        "Pending for approval",
        "",
        "",
        "",
        "Total",
        "",
        "",
        "",
        "Approved by DDE",
        "",
        "",
        "",
        "Pending for approval",
        "",
        "",
        "",
        "Total",
        "",
        "",
        "",
        "Total",
        "",
        "",
        "",
      ];

      const headerRow3 = [
        "",
        "A0",
        "A1",
        "A2",
        "Total",
        "A0",
        "A1",
        "A2",
        "Total",
        "A0",
        "A1",
        "A2",
        "Total",
        "A0",
        "A1",
        "A2",
        "Total",
        "A0",
        "A1",
        "A2",
        "Total",
        "A0",
        "A1",
        "A2",
        "Total",
        "A0",
        "A1",
        "A2",
        "Total",
      ];

      excelData.push(headerRow1);
      excelData.push(headerRow2);
      excelData.push(headerRow3);

      filteredData.forEach((item) => {
        if (item.Period !== "All requests") {
          const row = [
            item.Period,
            item.ApprovedByHT_A0 || 0,
            item.ApprovedByHT_A1 || 0,
            item.ApprovedByHT_A2 || 0,
            item.ApprovedByHT_Total || 0,
            item.PendingApproval_A0 || 0,
            item.PendingApproval_A1 || 0,
            item.PendingApproval_A2 || 0,
            item.PendingApproval_Total || 0,
            item.Total_A0 || 0,
            item.Total_A1 || 0,
            item.Total_A2 || 0,
            item.Total_Requests || 0,
            item.replacingStaff.ApprovedByDDE_A0 || 0,
            item.replacingStaff.ApprovedByDDE_A1 || 0,
            item.replacingStaff.ApprovedByDDE_A2 || 0,
            item.replacingStaff.ApprovedByDDE_Total || 0,
            item.replacingStaff.PendingApproval_A0 || 0,
            item.replacingStaff.PendingApproval_A1 || 0,
            item.replacingStaff.PendingApproval_A2 || 0,
            item.replacingStaff.PendingApproval_Total || 0,
            (item.replacingStaff.ApprovedByDDE_A0 || 0) +
              (item.replacingStaff.PendingApproval_A0 || 0),
            (item.replacingStaff.ApprovedByDDE_A1 || 0) +
              (item.replacingStaff.PendingApproval_A1 || 0),
            (item.replacingStaff.ApprovedByDDE_A2 || 0) +
              (item.replacingStaff.PendingApproval_A2 || 0),
            (item.replacingStaff.ApprovedByDDE_Total || 0) +
              (item.replacingStaff.PendingApproval_Total || 0),
            item.PlacedCandidates_A0 || 0,
            item.PlacedCandidates_A1 || 0,
            item.PlacedCandidates_A2 || 0,
            item.PlacedCandidates_Total || 0,
          ];
          excelData.push(row);
        }
      });

      const totalsItem = filteredData.find(
        (item) => item.Period === "All requests"
      );
      if (totalsItem) {
        const totalsRow = [
          "All requests",
          totalsItem.ApprovedByHT_A0 || 0,
          totalsItem.ApprovedByHT_A1 || 0,
          totalsItem.ApprovedByHT_A2 || 0,
          totalsItem.ApprovedByHT_Total || 0,
          totalsItem.PendingApproval_A0 || 0,
          totalsItem.PendingApproval_A1 || 0,
          totalsItem.PendingApproval_A2 || 0,
          totalsItem.PendingApproval_Total || 0,
          totalsItem.Total_A0 || 0,
          totalsItem.Total_A1 || 0,
          totalsItem.Total_A2 || 0,
          totalsItem.Total_Requests || 0,
          totalsItem.replacingStaff?.ApprovedByDDE_A0 || 0,
          totalsItem.replacingStaff?.ApprovedByDDE_A1 || 0,
          totalsItem.replacingStaff?.ApprovedByDDE_A2 || 0,
          totalsItem.replacingStaff?.ApprovedByDDE_Total || 0,
          totalsItem.replacingStaff?.PendingApproval_A0 || 0,
          totalsItem.replacingStaff?.PendingApproval_A1 || 0,
          totalsItem.replacingStaff?.PendingApproval_A2 || 0,
          totalsItem.replacingStaff?.PendingApproval_Total || 0,
          (totalsItem.replacingStaff?.ApprovedByDDE_A0 || 0) +
            (totalsItem.replacingStaff?.PendingApproval_A0 || 0),
          (totalsItem.replacingStaff?.ApprovedByDDE_A1 || 0) +
            (totalsItem.replacingStaff?.PendingApproval_A1 || 0),
          (totalsItem.replacingStaff?.ApprovedByDDE_A2 || 0) +
            (totalsItem.replacingStaff?.PendingApproval_A2 || 0),
          (totalsItem.replacingStaff?.ApprovedByDDE_Total || 0) +
            (totalsItem.replacingStaff?.PendingApproval_Total || 0),
          totalsItem.PlacedCandidates_A0 || 0,
          totalsItem.PlacedCandidates_A1 || 0,
          totalsItem.PlacedCandidates_A2 || 0,
          totalsItem.PlacedCandidates_Total || 0,
        ];
        excelData.push(totalsRow);
      }

      const ws = XLSX.utils.aoa_to_sheet(excelData);

      const colWidths = [{ wch: 15 }, ...Array(28).fill({ wch: 8 })];
      ws["!cols"] = colWidths;

      const merges = [
        { s: { r: 0, c: 1 }, e: { r: 0, c: 12 } },
        { s: { r: 0, c: 13 }, e: { r: 0, c: 24 } },
        { s: { r: 0, c: 25 }, e: { r: 0, c: 28 } },
        { s: { r: 1, c: 1 }, e: { r: 1, c: 4 } },
        { s: { r: 1, c: 5 }, e: { r: 1, c: 8 } },
        { s: { r: 1, c: 9 }, e: { r: 1, c: 12 } },
        { s: { r: 1, c: 13 }, e: { r: 1, c: 16 } },
        { s: { r: 1, c: 17 }, e: { r: 1, c: 20 } },
        { s: { r: 1, c: 21 }, e: { r: 1, c: 24 } },
        { s: { r: 1, c: 25 }, e: { r: 1, c: 28 } },
        { s: { r: 0, c: 0 }, e: { r: 2, c: 0 } },
      ];

      ws["!merges"] = merges;

      const headerStyle = {
        font: { bold: true },
        alignment: { horizontal: "center", vertical: "center" },
        fill: { fgColor: { rgb: "E6F3FF" } },
        border: {
          top: { style: "thin" },
          bottom: { style: "thin" },
          left: { style: "thin" },
          right: { style: "thin" },
        },
      };

      for (let row = 0; row < 3; row++) {
        for (let col = 0; col < headerRow3.length; col++) {
          const cellRef = XLSX.utils.encode_cell({ r: row, c: col });
          if (!ws[cellRef]) ws[cellRef] = { t: "s", v: "" };
          ws[cellRef].s = headerStyle;
        }
      }

      for (let row = 3; row < excelData.length; row++) {
        for (let col = 1; col < excelData[row].length; col++) {
          const cellRef = XLSX.utils.encode_cell({ r: row, c: col });
          if (ws[cellRef] && typeof ws[cellRef].v === "number") {
            ws[cellRef].z = "#,##0";
          }
        }
      }

      XLSX.utils.book_append_sheet(wb, ws, "Maternity Leave Report");

      const date = new Date().toISOString().split("T")[0];
      const filename = `Maternity Leave Distributions by Period Report As of ${date}.xlsx`;

      XLSX.writeFile(wb, filename);
    };

    if (!processedData || processedData.length === 0) {
      return (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="200px"
        >
          <Typography variant="h6" color="textSecondary">
            No maternity leave data available
          </Typography>
        </Box>
      );
    }

    return (
      <Box sx={{ width: "100%", overflowX: "auto" }}>
        <div style={{ width: "100%", overflowX: "auto" }}>
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              fontSize: "0.875rem",
              minWidth: "1400px",
            }}
          >
            <thead>
              <tr style={{ backgroundColor: "#f0f8ff" }}>
                <th
                  rowSpan={3}
                  style={{
                    border: "1px solid #ddd",
                    padding: "12px",
                    textAlign: "center",
                    fontSize: "16px",
                    fontWeight: "bold",
                    verticalAlign: "middle",
                    backgroundColor: "#e6f3ff",
                    minWidth: "120px",
                  }}
                >
                  Period
                </th>
                <th
                  colSpan={12}
                  style={{
                    border: "1px solid #ddd",
                    padding: "6px",
                    textAlign: "center",
                    fontSize: "14px",
                    fontWeight: "bold",
                    backgroundColor: "#e6f3ff",
                  }}
                >
                  Maternity Leave requests
                </th>
                <th
                  colSpan={12}
                  style={{
                    border: "1px solid #ddd",
                    padding: "6px",
                    textAlign: "center",
                    fontSize: "14px",
                    fontWeight: "bold",
                    backgroundColor: "#e6f3ff",
                  }}
                >
                  Waiting to be placed
                </th>
                <th
                  colSpan={4}
                  style={{
                    border: "1px solid #ddd",
                    padding: "6px",
                    textAlign: "center",
                    fontSize: "14px",
                    fontWeight: "bold",
                    backgroundColor: "#e6f3ff",
                  }}
                >
                  Placed candidates
                </th>
              </tr>

              <tr style={{ backgroundColor: "#e6f3ff" }}>
                <th
                  colSpan={4}
                  style={{
                    border: "1px solid #ddd",
                    padding: "6px",
                    textAlign: "center",
                    fontSize: "14px",
                    fontWeight: "bold",
                  }}
                >
                  Approved by HT
                </th>
                <th
                  colSpan={4}
                  style={{
                    border: "1px solid #ddd",
                    padding: "6px",
                    textAlign: "center",
                    fontSize: "14px",
                    fontWeight: "bold",
                  }}
                >
                  Pending for approval
                </th>
                <th
                  colSpan={4}
                  style={{
                    border: "1px solid #ddd",
                    padding: "6px",
                    textAlign: "center",
                    fontSize: "14px",
                    fontWeight: "bold",
                  }}
                >
                  Total
                </th>
                <th
                  colSpan={4}
                  style={{
                    border: "1px solid #ddd",
                    padding: "6px",
                    textAlign: "center",
                    fontSize: "14px",
                    fontWeight: "bold",
                  }}
                >
                  Approved by DDE
                </th>
                <th
                  colSpan={4}
                  style={{
                    border: "1px solid #ddd",
                    padding: "6px",
                    textAlign: "center",
                    fontSize: "14px",
                    fontWeight: "bold",
                  }}
                >
                  Pending for approval
                </th>
                <th
                  colSpan={4}
                  style={{
                    border: "1px solid #ddd",
                    padding: "6px",
                    textAlign: "center",
                    fontSize: "14px",
                    fontWeight: "bold",
                  }}
                >
                  Total
                </th>
                <th
                  colSpan={4}
                  style={{
                    border: "1px solid #ddd",
                    padding: "6px",
                    textAlign: "center",
                    fontSize: "14px",
                    fontWeight: "bold",
                  }}
                >
                  Total
                </th>
              </tr>

              <tr style={{ backgroundColor: "#e6f3ff" }}>
                <th
                  style={{
                    border: "1px solid #ddd",
                    padding: "4px",
                    textAlign: "center",
                    fontSize: "14px",
                    fontWeight: "bold",
                  }}
                >
                  A0
                </th>
                <th
                  style={{
                    border: "1px solid #ddd",
                    padding: "4px",
                    textAlign: "center",
                    fontSize: "14px",
                    fontWeight: "bold",
                  }}
                >
                  A1
                </th>
                <th
                  style={{
                    border: "1px solid #ddd",
                    padding: "4px",
                    textAlign: "center",
                    fontSize: "14px",
                    fontWeight: "bold",
                  }}
                >
                  A2
                </th>
                <th
                  style={{
                    border: "1px solid #ddd",
                    padding: "4px",
                    textAlign: "center",
                    fontSize: "14px",
                    fontWeight: "bold",
                  }}
                >
                  Total
                </th>

                <th
                  style={{
                    border: "1px solid #ddd",
                    padding: "4px",
                    textAlign: "center",
                    fontSize: "14px",
                    fontWeight: "bold",
                  }}
                >
                  A0
                </th>
                <th
                  style={{
                    border: "1px solid #ddd",
                    padding: "4px",
                    textAlign: "center",
                    fontSize: "14px",
                    fontWeight: "bold",
                  }}
                >
                  A1
                </th>
                <th
                  style={{
                    border: "1px solid #ddd",
                    padding: "4px",
                    textAlign: "center",
                    fontSize: "14px",
                    fontWeight: "bold",
                  }}
                >
                  A2
                </th>
                <th
                  style={{
                    border: "1px solid #ddd",
                    padding: "4px",
                    textAlign: "center",
                    fontSize: "14px",
                    fontWeight: "bold",
                  }}
                >
                  Total
                </th>

                <th
                  style={{
                    border: "1px solid #ddd",
                    padding: "4px",
                    textAlign: "center",
                    fontSize: "14px",
                    fontWeight: "bold",
                  }}
                >
                  A0
                </th>
                <th
                  style={{
                    border: "1px solid #ddd",
                    padding: "4px",
                    textAlign: "center",
                    fontSize: "14px",
                    fontWeight: "bold",
                  }}
                >
                  A1
                </th>
                <th
                  style={{
                    border: "1px solid #ddd",
                    padding: "4px",
                    textAlign: "center",
                    fontSize: "14px",
                    fontWeight: "bold",
                  }}
                >
                  A2
                </th>
                <th
                  style={{
                    border: "1px solid #ddd",
                    padding: "4px",
                    textAlign: "center",
                    fontSize: "14px",
                    fontWeight: "bold",
                  }}
                >
                  Total
                </th>

                <th
                  style={{
                    border: "1px solid #ddd",
                    padding: "4px",
                    textAlign: "center",
                    fontSize: "14px",
                    fontWeight: "bold",
                  }}
                >
                  A0
                </th>
                <th
                  style={{
                    border: "1px solid #ddd",
                    padding: "4px",
                    textAlign: "center",
                    fontSize: "14px",
                    fontWeight: "bold",
                  }}
                >
                  A1
                </th>
                <th
                  style={{
                    border: "1px solid #ddd",
                    padding: "4px",
                    textAlign: "center",
                    fontSize: "14px",
                    fontWeight: "bold",
                  }}
                >
                  A2
                </th>
                <th
                  style={{
                    border: "1px solid #ddd",
                    padding: "4px",
                    textAlign: "center",
                    fontSize: "14px",
                    fontWeight: "bold",
                  }}
                >
                  Total
                </th>

                <th
                  style={{
                    border: "1px solid #ddd",
                    padding: "4px",
                    textAlign: "center",
                    fontSize: "14px",
                    fontWeight: "bold",
                  }}
                >
                  A0
                </th>
                <th
                  style={{
                    border: "1px solid #ddd",
                    padding: "4px",
                    textAlign: "center",
                    fontSize: "14px",
                    fontWeight: "bold",
                  }}
                >
                  A1
                </th>
                <th
                  style={{
                    border: "1px solid #ddd",
                    padding: "4px",
                    textAlign: "center",
                    fontSize: "14px",
                    fontWeight: "bold",
                  }}
                >
                  A2
                </th>
                <th
                  style={{
                    border: "1px solid #ddd",
                    padding: "4px",
                    textAlign: "center",
                    fontSize: "14px",
                    fontWeight: "bold",
                  }}
                >
                  Total
                </th>

                <th
                  style={{
                    border: "1px solid #ddd",
                    padding: "4px",
                    textAlign: "center",
                    fontSize: "14px",
                    fontWeight: "bold",
                  }}
                >
                  A0
                </th>
                <th
                  style={{
                    border: "1px solid #ddd",
                    padding: "4px",
                    textAlign: "center",
                    fontSize: "14px",
                    fontWeight: "bold",
                  }}
                >
                  A1
                </th>
                <th
                  style={{
                    border: "1px solid #ddd",
                    padding: "4px",
                    textAlign: "center",
                    fontSize: "14px",
                    fontWeight: "bold",
                  }}
                >
                  A2
                </th>
                <th
                  style={{
                    border: "1px solid #ddd",
                    padding: "4px",
                    textAlign: "center",
                    fontSize: "14px",
                    fontWeight: "bold",
                  }}
                >
                  Total
                </th>

                <th
                  style={{
                    border: "1px solid #ddd",
                    padding: "4px",
                    textAlign: "center",
                    fontSize: "14px",
                    fontWeight: "bold",
                  }}
                >
                  A0
                </th>
                <th
                  style={{
                    border: "1px solid #ddd",
                    padding: "4px",
                    textAlign: "center",
                    fontSize: "14px",
                    fontWeight: "bold",
                  }}
                >
                  A1
                </th>
                <th
                  style={{
                    border: "1px solid #ddd",
                    padding: "4px",
                    textAlign: "center",
                    fontSize: "14px",
                    fontWeight: "bold",
                  }}
                >
                  A2
                </th>
                <th
                  style={{
                    border: "1px solid #ddd",
                    padding: "4px",
                    textAlign: "center",
                    fontSize: "14px",
                    fontWeight: "bold",
                  }}
                >
                  Total
                </th>
              </tr>
            </thead>

            <tbody>
              {filteredData
                .filter((item) => item.Period !== "All requests")
                .map((item, index) => (
                  <tr
                    key={item.Period}
                    style={{
                      backgroundColor: "#fff",
                    }}
                  >
                    <td
                      style={{
                        border: "1px solid #ddd",
                        padding: "8px",
                        textAlign: "left",
                        fontSize: "0.9rem",
                        fontWeight: "bold",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {item.Period}
                    </td>

                    <td
                      style={{
                        border: "1px solid #ddd",
                        padding: "8px",
                        textAlign: "center",
                        fontSize: "0.85rem",
                      }}
                    >
                      {(item.ApprovedByHT_A0 || 0).toLocaleString()}
                    </td>
                    <td
                      style={{
                        border: "1px solid #ddd",
                        padding: "8px",
                        textAlign: "center",
                        fontSize: "0.85rem",
                      }}
                    >
                      {(item.ApprovedByHT_A1 || 0).toLocaleString()}
                    </td>
                    <td
                      style={{
                        border: "1px solid #ddd",
                        padding: "8px",
                        textAlign: "center",
                        fontSize: "0.85rem",
                      }}
                    >
                      {(item.ApprovedByHT_A2 || 0).toLocaleString()}
                    </td>
                    <td
                      style={{
                        border: "1px solid #ddd",
                        padding: "8px",
                        textAlign: "center",
                        fontSize: "0.85rem",
                        fontWeight: "bold",
                      }}
                    >
                      {(item.ApprovedByHT_Total || 0).toLocaleString()}
                    </td>

                    <td
                      style={{
                        border: "1px solid #ddd",
                        padding: "8px",
                        textAlign: "center",
                        fontSize: "0.85rem",
                      }}
                    >
                      {(item.PendingApproval_A0 || 0).toLocaleString()}
                    </td>
                    <td
                      style={{
                        border: "1px solid #ddd",
                        padding: "8px",
                        textAlign: "center",
                        fontSize: "0.85rem",
                      }}
                    >
                      {(item.PendingApproval_A1 || 0).toLocaleString()}
                    </td>
                    <td
                      style={{
                        border: "1px solid #ddd",
                        padding: "8px",
                        textAlign: "center",
                        fontSize: "0.85rem",
                      }}
                    >
                      {(item.PendingApproval_A2 || 0).toLocaleString()}
                    </td>
                    <td
                      style={{
                        border: "1px solid #ddd",
                        padding: "8px",
                        textAlign: "center",
                        fontSize: "0.85rem",
                        fontWeight: "bold",
                      }}
                    >
                      {(item.PendingApproval_Total || 0).toLocaleString()}
                    </td>

                    <td
                      style={{
                        border: "1px solid #ddd",
                        padding: "8px",
                        textAlign: "center",
                        fontSize: "0.85rem",
                      }}
                    >
                      {(item.Total_A0 || 0).toLocaleString()}
                    </td>
                    <td
                      style={{
                        border: "1px solid #ddd",
                        padding: "8px",
                        textAlign: "center",
                        fontSize: "0.85rem",
                      }}
                    >
                      {(item.Total_A1 || 0).toLocaleString()}
                    </td>
                    <td
                      style={{
                        border: "1px solid #ddd",
                        padding: "8px",
                        textAlign: "center",
                        fontSize: "0.85rem",
                      }}
                    >
                      {(item.Total_A2 || 0).toLocaleString()}
                    </td>
                    <td
                      style={{
                        border: "1px solid #ddd",
                        padding: "8px",
                        textAlign: "center",
                        fontSize: "0.85rem",
                        fontWeight: "bold",
                        backgroundColor: "#f8f9fa",
                      }}
                    >
                      {(item.Total_Requests || 0).toLocaleString()}
                    </td>

                    <td
                      style={{
                        border: "1px solid #ddd",
                        padding: "8px",
                        textAlign: "center",
                        fontSize: "0.85rem",
                      }}
                    >
                      {(
                        item.replacingStaff.ApprovedByDDE_A0 || 0
                      ).toLocaleString()}
                    </td>
                    <td
                      style={{
                        border: "1px solid #ddd",
                        padding: "8px",
                        textAlign: "center",
                        fontSize: "0.85rem",
                      }}
                    >
                      {(
                        item.replacingStaff.ApprovedByDDE_A1 || 0
                      ).toLocaleString()}
                    </td>
                    <td
                      style={{
                        border: "1px solid #ddd",
                        padding: "8px",
                        textAlign: "center",
                        fontSize: "0.85rem",
                      }}
                    >
                      {(
                        item.replacingStaff.ApprovedByDDE_A2 || 0
                      ).toLocaleString()}
                    </td>
                    <td
                      style={{
                        border: "1px solid #ddd",
                        padding: "8px",
                        textAlign: "center",
                        fontSize: "0.85rem",
                        fontWeight: "bold",
                      }}
                    >
                      {(
                        item.replacingStaff.ApprovedByDDE_Total || 0
                      ).toLocaleString()}
                    </td>

                    <td
                      style={{
                        border: "1px solid #ddd",
                        padding: "8px",
                        textAlign: "center",
                        fontSize: "0.85rem",
                      }}
                    >
                      {(
                        item.replacingStaff.PendingApproval_A0 || 0
                      ).toLocaleString()}
                    </td>
                    <td
                      style={{
                        border: "1px solid #ddd",
                        padding: "8px",
                        textAlign: "center",
                        fontSize: "0.85rem",
                      }}
                    >
                      {(
                        item.replacingStaff.PendingApproval_A1 || 0
                      ).toLocaleString()}
                    </td>
                    <td
                      style={{
                        border: "1px solid #ddd",
                        padding: "8px",
                        textAlign: "center",
                        fontSize: "0.85rem",
                      }}
                    >
                      {(
                        item.replacingStaff.PendingApproval_A2 || 0
                      ).toLocaleString()}
                    </td>
                    <td
                      style={{
                        border: "1px solid #ddd",
                        padding: "8px",
                        textAlign: "center",
                        fontSize: "0.85rem",
                        fontWeight: "bold",
                      }}
                    >
                      {(
                        item.replacingStaff.PendingApproval_Total || 0
                      ).toLocaleString()}
                    </td>

                    <td
                      style={{
                        border: "1px solid #ddd",
                        padding: "8px",
                        textAlign: "center",
                        fontSize: "0.85rem",
                      }}
                    >
                      {(
                        (item.replacingStaff.ApprovedByDDE_A0 || 0) +
                        (item.replacingStaff.PendingApproval_A0 || 0)
                      ).toLocaleString()}
                    </td>
                    <td
                      style={{
                        border: "1px solid #ddd",
                        padding: "8px",
                        textAlign: "center",
                        fontSize: "0.85rem",
                      }}
                    >
                      {(
                        (item.replacingStaff.ApprovedByDDE_A1 || 0) +
                        (item.replacingStaff.PendingApproval_A1 || 0)
                      ).toLocaleString()}
                    </td>
                    <td
                      style={{
                        border: "1px solid #ddd",
                        padding: "8px",
                        textAlign: "center",
                        fontSize: "0.85rem",
                      }}
                    >
                      {(
                        (item.replacingStaff.ApprovedByDDE_A2 || 0) +
                        (item.replacingStaff.PendingApproval_A2 || 0)
                      ).toLocaleString()}
                    </td>
                    <td
                      style={{
                        border: "1px solid #ddd",
                        padding: "8px",
                        textAlign: "center",
                        fontSize: "0.85rem",
                        fontWeight: "bold",
                        backgroundColor: "#f8f9fa",
                      }}
                    >
                      {(
                        (item.replacingStaff.ApprovedByDDE_Total || 0) +
                        (item.replacingStaff.PendingApproval_Total || 0)
                      ).toLocaleString()}
                    </td>

                    <td
                      style={{
                        border: "1px solid #ddd",
                        padding: "8px",
                        textAlign: "center",
                        fontSize: "0.85rem",
                      }}
                    >
                      {(item.PlacedCandidates_A0 || 0).toLocaleString()}
                    </td>
                    <td
                      style={{
                        border: "1px solid #ddd",
                        padding: "8px",
                        textAlign: "center",
                        fontSize: "0.85rem",
                      }}
                    >
                      {(item.PlacedCandidates_A1 || 0).toLocaleString()}
                    </td>
                    <td
                      style={{
                        border: "1px solid #ddd",
                        padding: "8px",
                        textAlign: "center",
                        fontSize: "0.85rem",
                      }}
                    >
                      {(item.PlacedCandidates_A2 || 0).toLocaleString()}
                    </td>
                    <td
                      style={{
                        border: "1px solid #ddd",
                        padding: "8px",
                        textAlign: "center",
                        fontSize: "0.85rem",
                        fontWeight: "bold",
                        backgroundColor: "#f8f9fa",
                      }}
                    >
                      {(item.PlacedCandidates_Total || 0).toLocaleString()}
                    </td>
                  </tr>
                ))}

              {(() => {
                const totalsItem = filteredData.find(
                  (item) => item.Period === "All requests"
                );
                if (!totalsItem) return null;

                return (
                  <tr
                    style={{
                      backgroundColor: "#015198",
                      fontWeight: "bold",
                      color: "white",
                    }}
                  >
                    <td
                      style={{
                        border: "1px solid #ddd",
                        padding: "8px",
                        textAlign: "center",
                        fontSize: "0.85rem",
                        fontWeight: "bold",
                        backgroundColor: "#015198",
                        color: "white",
                      }}
                    >
                      All requests
                    </td>

                    <td
                      style={{
                        border: "1px solid #ddd",
                        padding: "8px",
                        textAlign: "center",
                        fontSize: "0.85rem",
                        backgroundColor: "#015198",
                        color: "white",
                      }}
                    >
                      {(totalsItem.ApprovedByHT_A0 || 0).toLocaleString()}
                    </td>
                    <td
                      style={{
                        border: "1px solid #ddd",
                        padding: "8px",
                        textAlign: "center",
                        fontSize: "0.85rem",
                        backgroundColor: "#015198",
                        color: "white",
                      }}
                    >
                      {(totalsItem.ApprovedByHT_A1 || 0).toLocaleString()}
                    </td>
                    <td
                      style={{
                        border: "1px solid #ddd",
                        padding: "8px",
                        textAlign: "center",
                        fontSize: "0.85rem",
                        backgroundColor: "#015198",
                        color: "white",
                      }}
                    >
                      {(totalsItem.ApprovedByHT_A2 || 0).toLocaleString()}
                    </td>
                    <td
                      style={{
                        border: "1px solid #ddd",
                        padding: "8px",
                        textAlign: "center",
                        fontSize: "0.85rem",
                        backgroundColor: "#015198",
                        color: "white",
                      }}
                    >
                      {(totalsItem.ApprovedByHT_Total || 0).toLocaleString()}
                    </td>

                    <td
                      style={{
                        border: "1px solid #ddd",
                        padding: "8px",
                        textAlign: "center",
                        fontSize: "0.85rem",
                        backgroundColor: "#015198",
                        color: "white",
                      }}
                    >
                      {(totalsItem.PendingApproval_A0 || 0).toLocaleString()}
                    </td>
                    <td
                      style={{
                        border: "1px solid #ddd",
                        padding: "8px",
                        textAlign: "center",
                        fontSize: "0.85rem",
                        backgroundColor: "#015198",
                        color: "white",
                      }}
                    >
                      {(totalsItem.PendingApproval_A1 || 0).toLocaleString()}
                    </td>
                    <td
                      style={{
                        border: "1px solid #ddd",
                        padding: "8px",
                        textAlign: "center",
                        fontSize: "0.85rem",
                        backgroundColor: "#015198",
                        color: "white",
                      }}
                    >
                      {(totalsItem.PendingApproval_A2 || 0).toLocaleString()}
                    </td>
                    <td
                      style={{
                        border: "1px solid #ddd",
                        padding: "8px",
                        textAlign: "center",
                        fontSize: "0.85rem",
                        backgroundColor: "#015198",
                        color: "white",
                      }}
                    >
                      {(totalsItem.PendingApproval_Total || 0).toLocaleString()}
                    </td>

                    <td
                      style={{
                        border: "1px solid #ddd",
                        padding: "8px",
                        textAlign: "center",
                        fontSize: "0.85rem",
                        backgroundColor: "#015198",
                        color: "white",
                      }}
                    >
                      {(totalsItem.Total_A0 || 0).toLocaleString()}
                    </td>
                    <td
                      style={{
                        border: "1px solid #ddd",
                        padding: "8px",
                        textAlign: "center",
                        fontSize: "0.85rem",
                        backgroundColor: "#015198",
                        color: "white",
                      }}
                    >
                      {(totalsItem.Total_A1 || 0).toLocaleString()}
                    </td>
                    <td
                      style={{
                        border: "1px solid #ddd",
                        padding: "8px",
                        textAlign: "center",
                        fontSize: "0.85rem",
                        backgroundColor: "#015198",
                        color: "white",
                      }}
                    >
                      {(totalsItem.Total_A2 || 0).toLocaleString()}
                    </td>
                    <td
                      style={{
                        border: "1px solid #ddd",
                        padding: "8px",
                        textAlign: "center",
                        fontSize: "0.85rem",
                        backgroundColor: "rgb(237, 108, 2)",
                        color: "white",
                      }}
                    >
                      {(totalsItem.Total_Requests || 0).toLocaleString()}
                    </td>

                    <td
                      style={{
                        border: "1px solid #ddd",
                        padding: "8px",
                        textAlign: "center",
                        fontSize: "0.85rem",
                        backgroundColor: "#015198",
                        color: "white",
                      }}
                    >
                      {(
                        totalsItem.replacingStaff?.ApprovedByDDE_A0 || 0
                      ).toLocaleString()}
                    </td>
                    <td
                      style={{
                        border: "1px solid #ddd",
                        padding: "8px",
                        textAlign: "center",
                        fontSize: "0.85rem",
                        backgroundColor: "#015198",
                        color: "white",
                      }}
                    >
                      {(
                        totalsItem.replacingStaff?.ApprovedByDDE_A1 || 0
                      ).toLocaleString()}
                    </td>
                    <td
                      style={{
                        border: "1px solid #ddd",
                        padding: "8px",
                        textAlign: "center",
                        fontSize: "0.85rem",
                        backgroundColor: "#015198",
                        color: "white",
                      }}
                    >
                      {(
                        totalsItem.replacingStaff?.ApprovedByDDE_A2 || 0
                      ).toLocaleString()}
                    </td>
                    <td
                      style={{
                        border: "1px solid #ddd",
                        padding: "8px",
                        textAlign: "center",
                        fontSize: "0.85rem",
                        backgroundColor: "#015198",
                        color: "white",
                      }}
                    >
                      {(
                        totalsItem.replacingStaff?.ApprovedByDDE_Total || 0
                      ).toLocaleString()}
                    </td>

                    <td
                      style={{
                        border: "1px solid #ddd",
                        padding: "8px",
                        textAlign: "center",
                        fontSize: "0.85rem",
                        backgroundColor: "#015198",
                        color: "white",
                      }}
                    >
                      {(
                        totalsItem.replacingStaff?.PendingApproval_A0 || 0
                      ).toLocaleString()}
                    </td>
                    <td
                      style={{
                        border: "1px solid #ddd",
                        padding: "8px",
                        textAlign: "center",
                        fontSize: "0.85rem",
                        backgroundColor: "#015198",
                        color: "white",
                      }}
                    >
                      {(
                        totalsItem.replacingStaff?.PendingApproval_A1 || 0
                      ).toLocaleString()}
                    </td>
                    <td
                      style={{
                        border: "1px solid #ddd",
                        padding: "8px",
                        textAlign: "center",
                        fontSize: "0.85rem",
                        backgroundColor: "#015198",
                        color: "white",
                      }}
                    >
                      {(
                        totalsItem.replacingStaff?.PendingApproval_A2 || 0
                      ).toLocaleString()}
                    </td>
                    <td
                      style={{
                        border: "1px solid #ddd",
                        padding: "8px",
                        textAlign: "center",
                        fontSize: "0.85rem",
                        backgroundColor: "#015198",
                        color: "white",
                      }}
                    >
                      {(
                        totalsItem.replacingStaff?.PendingApproval_Total || 0
                      ).toLocaleString()}
                    </td>

                    <td
                      style={{
                        border: "1px solid #ddd",
                        padding: "8px",
                        textAlign: "center",
                        fontSize: "0.85rem",
                        backgroundColor: "#015198",
                        color: "white",
                      }}
                    >
                      {(
                        (totalsItem.replacingStaff?.ApprovedByDDE_A0 || 0) +
                        (totalsItem.replacingStaff?.PendingApproval_A0 || 0)
                      ).toLocaleString()}
                    </td>
                    <td
                      style={{
                        border: "1px solid #ddd",
                        padding: "8px",
                        textAlign: "center",
                        fontSize: "0.85rem",
                        backgroundColor: "#015198",
                        color: "white",
                      }}
                    >
                      {(
                        (totalsItem.replacingStaff?.ApprovedByDDE_A1 || 0) +
                        (totalsItem.replacingStaff?.PendingApproval_A1 || 0)
                      ).toLocaleString()}
                    </td>
                    <td
                      style={{
                        border: "1px solid #ddd",
                        padding: "8px",
                        textAlign: "center",
                        fontSize: "0.85rem",
                        backgroundColor: "#015198",
                        color: "white",
                      }}
                    >
                      {(
                        (totalsItem.replacingStaff?.ApprovedByDDE_A2 || 0) +
                        (totalsItem.replacingStaff?.PendingApproval_A2 || 0)
                      ).toLocaleString()}
                    </td>
                    <td
                      style={{
                        border: "1px solid #ddd",
                        padding: "8px",
                        textAlign: "center",
                        fontSize: "0.85rem",
                        backgroundColor: "rgb(237, 108, 2)",
                        color: "white",
                      }}
                    >
                      {(
                        (totalsItem.replacingStaff?.ApprovedByDDE_Total || 0) +
                        (totalsItem.replacingStaff?.PendingApproval_Total || 0)
                      ).toLocaleString()}
                    </td>

                    <td
                      style={{
                        border: "1px solid #ddd",
                        padding: "8px",
                        textAlign: "center",
                        fontSize: "0.85rem",
                        backgroundColor: "#015198",
                        color: "white",
                      }}
                    >
                      {(totalsItem.PlacedCandidates_A0 || 0).toLocaleString()}
                    </td>
                    <td
                      style={{
                        border: "1px solid #ddd",
                        padding: "8px",
                        textAlign: "center",
                        fontSize: "0.85rem",
                        backgroundColor: "#015198",
                        color: "white",
                      }}
                    >
                      {(totalsItem.PlacedCandidates_A1 || 0).toLocaleString()}
                    </td>
                    <td
                      style={{
                        border: "1px solid #ddd",
                        padding: "8px",
                        textAlign: "center",
                        fontSize: "0.85rem",
                        backgroundColor: "#015198",
                        color: "white",
                      }}
                    >
                      {(totalsItem.PlacedCandidates_A2 || 0).toLocaleString()}
                    </td>
                    <td
                      style={{
                        border: "1px solid #ddd",
                        padding: "8px",
                        textAlign: "center",
                        fontSize: "0.85rem",
                        backgroundColor: "rgb(237, 108, 2)",
                        color: "white",
                      }}
                    >
                      {(
                        totalsItem.PlacedCandidates_Total || 0
                      ).toLocaleString()}
                    </td>
                  </tr>
                );
              })()}
            </tbody>
          </table>
        </div>
      </Box>
    );
  }
);

MaternityLeaveRequestsByPeriodTable.displayName = "MaternityLeaveRequestsTable";

export default MaternityLeaveRequestsByPeriodTable;
